import { useState } from 'react';
import axios from 'axios';
import { X, Calendar, Clock, MapPin, User, Mail, Phone, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingForm = ({ isOpen, onClose, selectedService }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: selectedService || '',
    preferred_date: '',
    preferred_time: '',
    address: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = ['Junk Removal', 'Deliveries', 'Small to Medium Size Moving'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/bookings`, formData);
      toast.success('Booking submitted successfully! We\'ll contact you soon.');
      onClose();
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        preferred_date: '',
        preferred_time: '',
        address: '',
        description: ''
      });
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="booking-form-modal">
      <div className="bg-[#1a1a1a] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#1a1a1a] border-b border-gray-800 p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold gold-gradient">Book Your Service</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-booking-form">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center">
                <User className="w-4 h-4 mr-2 text-[#d4af37]" />
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white"
                placeholder="John Doe"
                data-testid="booking-name"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-[#d4af37]" />
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white"
                  placeholder="john@example.com"
                  data-testid="booking-email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-[#d4af37]" />
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white"
                  placeholder="(403) 555-0123"
                  data-testid="booking-phone"
                />
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Service Type *</label>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white"
                data-testid="booking-service"
              >
                <option value="">Select a service</option>
                {services.map(service => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </select>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-[#d4af37]" />
                  Preferred Date *
                </label>
                <input
                  type="date"
                  name="preferred_date"
                  value={formData.preferred_date}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white"
                  data-testid="booking-date"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-[#d4af37]" />
                  Preferred Time *
                </label>
                <select
                  name="preferred_time"
                  value={formData.preferred_time}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white"
                  data-testid="booking-time"
                >
                  <option value="">Select time</option>
                  <option value="Morning (8AM-12PM)">Morning (8AM-12PM)</option>
                  <option value="Afternoon (12PM-5PM)">Afternoon (12PM-5PM)</option>
                  <option value="Evening (5PM-8PM)">Evening (5PM-8PM)</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 flex items-center">
                <MapPin className="w-4 h-4 mr-2 text-[#d4af37]" />
                Service Address *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white"
                placeholder="123 Main St, Calgary, AB"
                data-testid="booking-address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 flex items-center">
                <FileText className="w-4 h-4 mr-2 text-[#d4af37]" />
                Additional Details
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white resize-none"
                placeholder="Please provide any additional details about your service needs..."
                data-testid="booking-description"
              />
            </div>
          </div>

          <div className="bg-black/50 p-4 rounded-lg border border-gray-800">
            <p className="text-sm text-gray-400">
              <strong className="text-[#d4af37]">Note:</strong> This is a booking request. We'll contact you within 24 hours to confirm availability and provide a quote for your service.
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#d4af37] text-black px-6 py-4 rounded-lg font-bold hover:bg-[#b8941f] transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-booking"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookingForm;